import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DollarSign, Users, Clock, CheckCircle2, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { formatDMY as formatDate } from "@/lib/dateFormat";

const STATUS = {
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  active: { label: "Activa", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelada", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export function CourseOrdersTab() {
  const confirm = useConfirm();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/course-orders").then((r) => setOrders(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const activate = async (o) => {
    const ok = await confirm({
      title: "¿Activar acceso?",
      description: `${o.name} (${o.email}) recibirá acceso inmediato a "${o.course_title}" y un email con su enlace de ingreso. Confirmá solo si ya recibiste el pago de $${Number(o.amount).toFixed(2)}.`,
      confirmText: "Sí, activar",
    });
    if (!ok) return;
    setBusy(o.id);
    try {
      const r = await api.post(`/course-orders/${o.id}/activate`);
      toast.success(r.data.user_created ? "Acceso activado — se creó la cuenta y se envió el enlace" : "Acceso activado y email enviado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(null); }
  };

  const cancel = async (o) => {
    const isActive = o.status === "active";
    const ok = await confirm({
      title: isActive ? "¿Revocar acceso?" : "¿Cancelar orden?",
      description: isActive
        ? `${o.name} perderá inmediatamente el acceso a "${o.course_title}". Su progreso se conserva por si lo reactivás después.`
        : `Se cancelará la orden de ${o.name}.`,
      confirmText: isActive ? "Sí, revocar" : "Sí, cancelar",
    });
    if (!ok) return;
    try {
      await api.post(`/course-orders/${o.id}/cancel`);
      toast.success(isActive ? "Acceso revocado" : "Orden cancelada");
      load();
    } catch { toast.error("Error"); }
  };

  return (
    <Card className="overflow-x-auto" data-testid="course-orders-tab">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
            <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Comprador</th><th className="px-4 py-3">Curso</th>
            <th className="px-4 py-3">Monto</th><th className="px-4 py-3">Afiliado</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin órdenes todavía</td></tr>}
          {orders.map((o) => {
            const st = STATUS[o.status] || STATUS.pending;
            return (
              <tr key={o.id} data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 text-xs text-slate-500">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3"><div className="font-medium text-slate-800">{o.name}</div><div className="text-xs text-slate-500">{o.email}{o.whatsapp ? ` · ${o.whatsapp}` : ""}</div></td>
                <td className="px-4 py-3 text-slate-700">{o.course_title}</td>
                <td className="px-4 py-3 font-mono">${Number(o.amount).toFixed(2)}</td>
                <td className="px-4 py-3 text-xs font-mono">{o.affiliate_code || "—"}</td>
                <td className="px-4 py-3"><Badge className={st.cls}>{st.label}</Badge></td>
                <td className="px-4 py-3 text-right">
                  {o.status === "pending" && (
                    <div className="inline-flex gap-1">
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" disabled={busy === o.id} onClick={() => activate(o)} data-testid={`activate-order-${o.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Activar
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-red-500 text-xs" onClick={() => cancel(o)} data-testid={`cancel-order-${o.id}`}><X className="h-3 w-3" /></Button>
                    </div>
                  )}
                  {o.status === "active" && (
                    <Button size="sm" variant="ghost" className="h-7 text-red-500 text-xs" onClick={() => cancel(o)} data-testid={`revoke-order-${o.id}`}>
                      <X className="h-3 w-3 mr-1" /> Revocar
                    </Button>
                  )}
                  {o.status === "cancelled" && (
                    <Button size="sm" variant="ghost" className="h-7 text-emerald-600 text-xs" disabled={busy === o.id} onClick={() => activate(o)} data-testid={`reactivate-order-${o.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Reactivar
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

export function CourseSalesTab() {
  const [data, setData] = useState(null);
  const [openCourse, setOpenCourse] = useState(null);
  const [students, setStudents] = useState({});

  useEffect(() => { api.get("/course-sales/summary").then((r) => setData(r.data)).catch(() => {}); }, []);

  const toggleStudents = async (courseId) => {
    if (openCourse === courseId) { setOpenCourse(null); return; }
    setOpenCourse(courseId);
    if (!students[courseId]) {
      try {
        const r = await api.get(`/courses/${courseId}/students`);
        setStudents((s) => ({ ...s, [courseId]: r.data || [] }));
      } catch { /* noop */ }
    }
  };

  if (!data) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="course-sales-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4"><div className="flex items-center gap-2 text-xs text-slate-500"><DollarSign className="h-4 w-4 text-emerald-600" /> Ingresos por cursos</div><div className="text-2xl font-bold text-slate-900 mt-1" data-testid="sales-total-revenue">${Number(data.totals.revenue).toFixed(2)}</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-xs text-slate-500"><Users className="h-4 w-4 text-brand-blue" /> Estudiantes activos</div><div className="text-2xl font-bold text-slate-900 mt-1">{data.totals.students}</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-xs text-slate-500"><Clock className="h-4 w-4 text-amber-500" /> Órdenes pendientes</div><div className="text-2xl font-bold text-slate-900 mt-1">{data.totals.pending_orders}</div></Card>
      </div>
      <div className="space-y-2">
        {data.courses.map((c) => (
          <Card key={c.course_id} className="overflow-hidden">
            <button type="button" className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => toggleStudents(c.course_id)} data-testid={`sales-course-${c.course_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 text-sm truncate">{c.title}</div>
                <div className="text-xs text-slate-500">{c.students} estudiantes · {c.pending_orders} pendientes · {c.price === 0 ? "Gratis" : c.price != null ? `$${Number(c.price).toFixed(0)}` : "sin precio"}</div>
              </div>
              <div className="font-mono font-semibold text-emerald-700">${Number(c.revenue).toFixed(2)}</div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openCourse === c.course_id ? "rotate-180" : ""}`} />
            </button>
            {openCourse === c.course_id && (
              <div className="border-t border-slate-100 divide-y divide-slate-50" data-testid={`sales-students-${c.course_id}`}>
                {(students[c.course_id] || []).length === 0 && <div className="px-4 py-4 text-xs text-slate-400">Sin estudiantes aún</div>}
                {(students[c.course_id] || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 truncate">{s.name}</div>
                      <div className="text-[11px] text-slate-400">{s.email}{s.last_activity ? ` · última actividad ${formatDate(s.last_activity)}` : ""}</div>
                    </div>
                    <div className="w-32"><Progress value={s.progress_pct} className="h-2" /></div>
                    <div className="text-xs font-medium text-slate-600 w-10 text-right">{s.progress_pct}%</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
